import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Search, RefreshCw, X, FileText, CreditCard, AlertTriangle,
         CheckCircle, Clock, Mail, ExternalLink, Send, Upload, Download,
         Trash2, Plus, ChevronDown } from 'lucide-react';
import { useRafraichissementAuto } from '../rafraichissementAuto';
import { useBlocageDefilement } from '../blocageDefilement';
import PanneauParcours, { NIVEAU } from './SuiviParcours';

/* Suivi des dossiers E-Mandat depuis le CRM.
   Le projet E-Mandat reste independant : cet ecran lit son API d'administration
   a travers le backend du CRM, il ne touche jamais sa base. */

const API = process.env.REACT_APP_API_URL || '/api';
const jetonEntete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const dateFr = (v) => (v ? new Date(String(v).replace(' ', 'T')).toLocaleDateString('fr-FR',
  { timeZone: 'Europe/Paris' }) : '—');
const dateHeure = (v) => (v ? new Date(String(v).replace(' ', 'T')).toLocaleString('fr-FR',
  { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit' }) : '—');
const euros = (v) => (Number(v) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const STATUT_CLIENT = {
  essai: { texte: 'Premier mois offert', fond: '#fff8e1', couleur: '#8d6e00' },
  actif: { texte: 'Abonnement actif', fond: '#e8f5e9', couleur: '#1b5e20' },
  resilie: { texte: 'Résilié', fond: '#eceff1', couleur: '#546e7a' },
  suspendu: { texte: 'Suspendu', fond: '#ffebee', couleur: '#b71c1c' }
};
const STATUT_SERVICE = {
  demande: { texte: 'À prendre en charge', fond: '#fff3e0', couleur: '#e65100' },
  en_cours: { texte: 'En cours', fond: '#e8f1ff', couleur: '#14448f' },
  fait: { texte: 'Effectué', fond: '#e8f5e9', couleur: '#1b5e20' },
  impossible: { texte: 'Impossible', fond: '#eceff1', couleur: '#7a2f2f' }
};
const STATUT_DEMANDE = {
  a_envoyer: 'À envoyer', envoyee: 'Envoyée', a_relancer: 'À relancer',
  repondue: 'Réponse reçue', annulee: 'Annulée'
};

function Pastille({ table, valeur }) {
  const s = table[valeur] || { texte: valeur, fond: '#eceff1', couleur: '#546e7a' };
  return <span style={{ background: s.fond, color: s.couleur, borderRadius: 999,
                        padding: '3px 10px', fontSize: 12, fontWeight: 600,
                        whiteSpace: 'nowrap' }}>{s.texte}</span>;
}

function Ligne({ libelle, valeur, mono }) {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f0f2f5' }}>
      <div style={{ width: 210, flex: 'none', color: '#78909c', fontSize: 12.5 }}>{libelle}</div>
      <div style={{ fontSize: 13.5, color: '#263238', wordBreak: 'break-word',
                    fontFamily: mono ? 'ui-monospace,Menlo,monospace' : 'inherit' }}>{String(valeur)}</div>
    </div>
  );
}

function Titre({ children, aide }) {
  return (
    <>
      <h4 style={{ margin: '20px 0 2px', fontSize: 13, textTransform: 'uppercase',
                   letterSpacing: '.05em', color: '#667eea' }}>{children}</h4>
      {aide && <p style={{ margin: '0 0 6px', fontSize: 12, color: '#90a4ae' }}>{aide}</p>}
    </>
  );
}

/* ───────────────────────── fiche complete d'un dossier ──────────────────── */
function Dossier({ clientId, onFermer, onChangement }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);

  const charger = async () => {
    try {
      const r = await fetch(`${API}/emandat/clients/${clientId}`, { headers: jetonEntete() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setD(j); setErreur(null);
    } catch (e) { setErreur(e.message); }
  };
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [clientId]);

  const [docs, setDocs] = useState([]);
  const [organismes, setOrganismes] = useState([]);
  const [manquantsOuverts, setManquants] = useState(false);
  const [saisie, setSaisie] = useState({});           // contacts saisis a la main
  const [depot, setDepot] = useState({ nature: 'identite' });
  const [motAdmin, setMotAdmin] = useState(null);     // message d'action

  const chargerDocs = async () => {
    try {
      const r = await fetch(`${API}/emandat/clients/${clientId}/documents`, { headers: jetonEntete() });
      const j = await r.json();
      if (r.ok) setDocs(j.documents || []);
    } catch (e) { /* on reessaiera */ }
  };
  const chargerOrganismes = async () => {
    try {
      const r = await fetch(`${API}/emandat/organismes`, { headers: jetonEntete() });
      const j = await r.json();
      if (r.ok) setOrganismes(j.organismes || []);
    } catch (e) { /* on reessaiera */ }
  };
  useEffect(() => { chargerDocs(); chargerOrganismes(); /* eslint-disable-next-line */ }, [clientId]);

  /* L'ENVOI : c'est le geste de validation. Rien ne part sans ce clic. */
  const envoyer = async (demandeId, sansIdentite) => {
    setEnCours(`envoi-${demandeId}`);
    setMotAdmin(null);
    try {
      const r = await fetch(`${API}/emandat/demandes/${demandeId}/envoyer`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sans_identite: sansIdentite || undefined })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMotAdmin({ type: 'ok', texte: `Demande envoyée à ${j.destinataire}` });
      await charger();
    } catch (e) { setMotAdmin({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  /* Une seule decision pour tout le dossier : on annonce d'abord ce qui va
     partir, parce qu'un envoi ne se rattrape pas. */
  const envoyerTout = async () => {
    const aEnvoyer = (d.demandes || []).filter((x) => x.statut === 'a_envoyer');
    const prets = aEnvoyer.filter((x) => x.email_dpo);
    if (!prets.length) {
      setMotAdmin({ type: 'ko', texte: 'Aucune demande envoyable : aucune adresse connue.' });
      return;
    }
    const sans = aEnvoyer.length - prets.length;
    if (!window.confirm(
      `Envoyer ${prets.length} demande${prets.length > 1 ? 's' : ''} maintenant, `
      + `au nom de ${d.client.prenom} ${d.client.nom} ?`
      + (sans ? `\n\n${sans} organisme${sans > 1 ? 's' : ''} sera ignoré faute d’adresse.` : '')
      + '\n\nCette action est irréversible.')) return;
    setEnCours('envoi-tout');
    setMotAdmin(null);
    try {
      const r = await fetch(`${API}/emandat/clients/${clientId}/envoyer-tout`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMotAdmin({ type: j.envoyees ? 'ok' : 'ko',
        texte: `${j.envoyees} demande(s) envoyée(s) sur ${j.total}`
          + (j.sans_adresse.length ? ` · ${j.sans_adresse.length} sans adresse` : '')
          + (j.echecs.length ? ` · ${j.echecs.length} en échec : ${j.echecs[0].raison}` : '') });
      await charger();
    } catch (e) { setMotAdmin({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  const enregistrerContact = async (organismeId) => {
    const v = saisie[organismeId] || {};
    if (!v.email_dpo && !v.adresse_postale) return;
    try {
      const r = await fetch(`${API}/emandat/organismes/${organismeId}/contact`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify(v)
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMotAdmin({ type: 'ok', texte: 'Adresse enregistrée' });
      setSaisie((x) => ({ ...x, [organismeId]: {} }));
      await chargerOrganismes(); await charger();
    } catch (e) { setMotAdmin({ type: 'ko', texte: e.message }); }
  };

  const deposer = async (fichiers) => {
    if (!fichiers || !fichiers.length) return;
    const f = new FormData();
    f.append('nature', depot.nature);
    for (const x of fichiers) f.append('fichier', x);
    setEnCours('depot');
    try {
      const r = await fetch(`${API}/emandat/clients/${clientId}/documents`, {
        method: 'POST', headers: jetonEntete(), body: f
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMotAdmin({ type: 'ok', texte: `${j.ajoutes} document(s) ajouté(s)` });
      await chargerDocs();
    } catch (e) { setMotAdmin({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  const avancer = async (type, id, statut) => {
    setEnCours(`${type}-${id}`);
    try {
      const r = await fetch(`${API}/emandat/${type}/${id}`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut })
      });
      if (!r.ok) { const j = await r.json().catch(() => null); throw new Error((j && j.error) || `Erreur ${r.status}`); }
      await charger();
      if (onChangement) onChangement();
    } catch (e) { setErreur(e.message); }
    setEnCours(null);
  };

  const bouton = (libelle, actif, action, couleur = '#667eea') => (
    <button onClick={action} disabled={!!enCours}
            style={{ border: `1px solid ${couleur}33`, background: actif ? couleur : '#fff',
                     color: actif ? '#fff' : couleur, borderRadius: 6, padding: '5px 11px',
                     fontSize: 12.5, fontWeight: 600, cursor: enCours ? 'default' : 'pointer' }}>
      {libelle}
    </button>
  );

  const c = d && d.client;
  return (
    <div onClick={onFermer}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,35,.55)', zIndex: 1000,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 10, width: 'min(880px,100%)',
                    boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '16px 20px', background: 'linear-gradient(135deg,#000091,#3a3ab8)',
                      color: '#fff' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {c ? `${c.prenom || ''} ${c.nom || ''}`.trim() || 'Dossier' : 'Chargement…'}</div>
            <div style={{ fontSize: 12.5, opacity: .85 }}>
              {c ? `Dossier n° ${c.id} · ouvert le ${dateFr(c.cree_le)}` : ''}</div>
          </div>
          <button onClick={onFermer} aria-label="Fermer"
                  style={{ background: 'rgba(255,255,255,.18)', border: 0, color: '#fff',
                           borderRadius: 6, padding: 7, cursor: 'pointer', lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '14px 20px 22px', maxHeight: '74vh', overflowY: 'auto' }}>
          {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '10px 14px',
                                   borderRadius: 6, marginBottom: 12 }}>{erreur}</div>}
          {!d ? <p style={{ color: '#90a4ae' }}>Chargement du dossier…</p> : (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <Pastille table={STATUT_CLIENT} valeur={c.statut} />
                {d.mandat && !d.mandat.revoque_le
                  ? <span style={{ background: '#e8f5e9', color: '#1b5e20', borderRadius: 999,
                                   padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>Mandat actif</span>
                  : <span style={{ background: '#ffebee', color: '#b71c1c', borderRadius: 999,
                                   padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>Mandat révoqué</span>}
                {d.services.some((s) => ['demande', 'en_cours'].includes(s.statut)) &&
                  <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 999,
                                 padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                    Action attendue de votre part</span>}
              </div>

              <Titre aide="Ce que le client a renseigné à l’inscription.">Son identité</Titre>
              <Ligne libelle="Nom et prénom" valeur={`${c.prenom || ''} ${c.nom || ''}`.trim()} />
              <Ligne libelle="Email" valeur={c.email} />
              <Ligne libelle="Téléphone" valeur={c.telephone} />
              <Ligne libelle="Adresse" valeur={c.adresse} />
              <Ligne libelle="Date de naissance" valeur={c.date_naissance ? dateFr(c.date_naissance) : null} />
              <Ligne libelle="Dossier ouvert le" valeur={dateHeure(c.cree_le)} />
              {d.acces[0] && (
                <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f0f2f5' }}>
                  <div style={{ width: 210, flex: 'none', color: '#78909c', fontSize: 12.5 }}>
                    Son espace personnel</div>
                  <a href={`https://protection.es-p.fr/espace/${d.acces[0].jeton}`} target="_blank"
                     rel="noopener noreferrer"
                     style={{ fontSize: 13, color: '#3949ab', display: 'inline-flex', gap: 5,
                              alignItems: 'center' }}>
                    Ouvrir l’espace du client <ExternalLink size={13} />
                  </a>
                </div>
              )}

              <Titre aide="Dates contractuelles et paiements enregistrés.">Son abonnement</Titre>
              <Ligne libelle="Statut" valeur={(STATUT_CLIENT[c.statut] || {}).texte || c.statut} />
              <Ligne libelle="Fin de rétractation" valeur={c.fin_retractation ? dateFr(c.fin_retractation) : 'non renseignée'} />
              <Ligne libelle="Fin du mois offert" valeur={c.fin_periode_offerte ? dateFr(c.fin_periode_offerte) : 'non renseignée'} />
              <Ligne libelle="Fin d’engagement" valeur={c.fin_engagement ? dateFr(c.fin_engagement) : 'non renseignée'} />
              {d.paiements.length === 0
                ? <p style={{ color: '#90a4ae', fontSize: 13, margin: '8px 0 0' }}>
                    Aucun paiement enregistré pour ce dossier.</p>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginTop: 8 }}>
                    <thead><tr style={{ background: '#f7f8fb' }}>
                      {['Date', 'Référence', 'Montant', 'Statut'].map((t) => (
                        <th key={t} style={{ textAlign: 'left', padding: '7px 9px', color: '#78909c',
                                             fontSize: 11, textTransform: 'uppercase' }}>{t}</th>))}
                    </tr></thead>
                    <tbody>
                      {d.paiements.map((p) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                          <td style={{ padding: '7px 9px' }}>{dateHeure(p.cree_le)}</td>
                          <td style={{ padding: '7px 9px', fontFamily: 'ui-monospace,monospace' }}>{p.reference}</td>
                          <td style={{ padding: '7px 9px' }}>{euros(p.montant)}</td>
                          <td style={{ padding: '7px 9px' }}>{p.statut}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              <Titre aide="Ce que le client vous a confié d’un clic depuis son espace. C’est ici que vous faites avancer son dossier.">
                Les démarches qu’il vous a confiées</Titre>
              {d.services.length === 0
                ? <p style={{ color: '#90a4ae', fontSize: 13 }}>Aucune démarche confiée pour le moment.</p>
                : d.services.map((s) => (
                  <div key={s.id} style={{ border: '1px solid #eceff1', borderRadius: 8,
                                           padding: '12px 14px', marginBottom: 10,
                                           background: s.statut === 'demande' ? '#fffdf7' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
                                  flexWrap: 'wrap', alignItems: 'center' }}>
                      <b style={{ color: '#263238' }}>{s.titre}</b>
                      <Pastille table={STATUT_SERVICE} valeur={s.statut} />
                    </div>
                    {s.detail && <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#546e7a' }}>{s.detail}</p>}
                    <p style={{ margin: '6px 0 8px', fontSize: 11.5, color: '#90a4ae' }}>
                      Confiée le {dateHeure(s.demande_le)}
                      {s.maj_le && s.maj_le !== s.demande_le ? ` · mise à jour le ${dateHeure(s.maj_le)}` : ''}
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {bouton('Je la prends en charge', s.statut === 'en_cours',
                              () => avancer('services', s.id, 'en_cours'))}
                      {bouton('Marquer effectuée', s.statut === 'fait',
                              () => avancer('services', s.id, 'fait'), '#2e7d32')}
                      {bouton('Impossible à faire', s.statut === 'impossible',
                              () => avancer('services', s.id, 'impossible'), '#c62828')}
                    </div>
                  </div>
                ))}

              <Titre aide="Les organismes interrogés en son nom, et l’échéance légale d’un mois pour chaque réponse.">
                Ses demandes d’accès ({d.demandes.length})</Titre>
              {(d.demandes || []).some((x) => x.statut === 'a_envoyer') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                              background: '#f5f7ff', border: '1px solid #dde3f7', borderRadius: 8,
                              padding: '10px 13px', marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: '#3a4358' }}>
                    <b>{(d.demandes || []).filter((x) => x.statut === 'a_envoyer' && x.email_dpo).length} demande(s)
                    prêtes à partir</b>
                    {(d.demandes || []).filter((x) => x.statut === 'a_envoyer' && !x.email_dpo).length > 0 && (
                      <> · {(d.demandes || []).filter((x) => x.statut === 'a_envoyer' && !x.email_dpo).length} en
                      attente d’adresse</>
                    )}
                  </div>
                  <button onClick={envoyerTout} disabled={enCours === 'envoi-tout'}
                          style={{ padding: '8px 14px', border: 'none', borderRadius: 6,
                                   background: '#000091', color: '#fff', fontSize: 13,
                                   fontWeight: 600, cursor: 'pointer', display: 'flex',
                                   alignItems: 'center', gap: 6 }}>
                    <Send size={14} />
                    {enCours === 'envoi-tout' ? 'Envoi en cours…' : 'Envoyer à tous les organismes'}
                  </button>
                </div>
              )}
              <div style={{ border: '1px solid #eceff1', borderRadius: 8, maxHeight: 280,
                            overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead><tr>
                    {['Organisme', 'Contact DPO', 'Statut', 'Échéance', 'Action'].map((t) => (
                      <th key={t} style={{ textAlign: 'left', padding: '8px 9px', background: '#f7f8fb',
                                           position: 'sticky', top: 0, color: '#78909c', fontSize: 11,
                                           textTransform: 'uppercase' }}>{t}</th>))}
                  </tr></thead>
                  <tbody>
                    {d.demandes.map((x) => (
                      <tr key={x.id} style={{ borderBottom: '1px solid #f3f5f8' }}>
                        <td style={{ padding: '7px 9px' }}>{x.organisme || '—'}</td>
                        <td style={{ padding: '7px 9px', color: x.email_dpo ? '#2e7d32' : '#c62828' }}>
                          {x.email_dpo || 'manquant'}</td>
                        <td style={{ padding: '7px 9px' }}>{STATUT_DEMANDE[x.statut] || x.statut}</td>
                        <td style={{ padding: '7px 9px' }}>{dateFr(x.echeance_legale)}</td>
                        <td style={{ padding: '7px 9px', whiteSpace: 'nowrap' }}>
                          {x.statut === 'a_envoyer' && (
                            <button onClick={() => envoyer(x.id)}
                                    disabled={!x.email_dpo || enCours === `envoi-${x.id}`}
                                    title={x.email_dpo
                                      ? 'Envoyer maintenant la demande à cet organisme'
                                      : 'Adresse manquante : renseignez-la plus bas'}
                                    style={{ padding: '5px 10px', border: 'none', borderRadius: 6,
                                             background: x.email_dpo ? '#000091' : '#cfd8dc',
                                             color: '#fff', fontSize: 12, fontWeight: 600,
                                             cursor: x.email_dpo ? 'pointer' : 'not-allowed',
                                             marginRight: 5 }}>
                              <Send size={12} style={{ verticalAlign: '-2px' }} />
                              {enCours === `envoi-${x.id}` ? ' Envoi…' : ' Envoyer'}
                            </button>
                          )}
                          {x.statut === 'a_envoyer' && bouton('Marquer envoyée', false,
                            () => avancer('demandes', x.id, 'envoyee'))}
                          {x.statut === 'envoyee' && bouton('Réponse reçue', false,
                            () => avancer('demandes', x.id, 'repondue'), '#2e7d32')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {d.reponses.length > 0 && (
                <>
                  <Titre>Les réponses reçues</Titre>
                  {d.reponses.map((r) => (
                    <div key={r.id} style={{ border: '1px solid #eceff1', borderRadius: 8,
                                             padding: '10px 13px', marginBottom: 8 }}>
                      <b style={{ fontSize: 13 }}>{r.organisme || 'Organisme'}</b>
                      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#546e7a' }}>
                        {r.contenu || r.resume || r.statut || '—'}</p>
                    </div>
                  ))}
                </>
              )}

              {d.fuites.length > 0 && (
                <>
                  <Titre aide="Apparitions de ses données dans des fuites connues.">Les fuites de données</Titre>
                  {d.fuites.map((f) => (
                    <Ligne key={f.id} libelle={f.source || 'Fuite'} valeur={f.detail || f.description || '—'} />
                  ))}
                </>
              )}

              <Titre aide="Le document signé qui vous autorise à agir en son nom. Sans lui, aucune démarche ne peut partir.">
                Son mandat</Titre>
              {d.mandat ? (
                <>
                  <Ligne libelle="Signé le" valeur={dateHeure(d.mandat.signe_le || d.mandat.cree_le)} />
                  <Ligne libelle="Adresse IP de signature" valeur={d.mandat.signature_ip} mono />
                  <Ligne libelle="Périmètre" valeur={d.mandat.perimetre} />
                  <Ligne libelle="Pièce d’identité" valeur={d.mandat.piece_identite || 'aucune pièce jointe'} />
                  <Ligne libelle="Révoqué le" valeur={d.mandat.revoque_le ? dateHeure(d.mandat.revoque_le) : 'non révoqué'} />
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', color: '#3949ab', fontSize: 12.5 }}>
                      Lire le texte du mandat</summary>
                    <pre style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 6,
                                  padding: 12, fontSize: 11.5, whiteSpace: 'pre-wrap', marginTop: 8 }}>
{d.mandat.texte}</pre>
                  </details>
                </>
              ) : <p style={{ color: '#c62828', fontSize: 13 }}>Aucun mandat : rien ne peut être fait en son nom.</p>}

              {motAdmin && (
                <div style={{ padding: '10px 13px', borderRadius: 8, margin: '12px 0', fontSize: 13,
                              background: motAdmin.type === 'ok' ? '#e8f5e9' : '#ffebee',
                              color: motAdmin.type === 'ok' ? '#1b5e20' : '#b71c1c' }}>
                  {motAdmin.texte}
                </div>
              )}

              <Titre aide="Les pièces du dossier. Le mandat est joint automatiquement à chaque demande ; la pièce d’identité l’est aussi dès qu’elle est fournie.">
                Ses documents ({docs.length})</Titre>
              {docs.length === 0
                ? <p style={{ color: '#90a4ae', fontSize: 13 }}>Aucun document.</p>
                : docs.map((x) => (
                  <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                           padding: '7px 0', borderBottom: '1px solid #f1f3f7',
                                           fontSize: 13 }}>
                    <FileText size={15} color={x.nature === 'mandat' ? '#2e7d32' : '#78909c'} />
                    <div style={{ flex: 1 }}>
                      <b>{x.nom_origine || x.nature}</b>
                      <span style={{ color: '#90a4ae', fontSize: 12 }}>
                        {' · '}{x.nature}{' · '}{Math.round((x.taille || 0) / 1024)} Ko
                        {' · '}{dateHeure(x.cree_le)}</span>
                    </div>
                    <a href={`${API}/emandat/documents/${x.id}`} target="_blank" rel="noreferrer"
                       style={{ color: '#3949ab' }} title="Ouvrir">
                      <Download size={15} />
                    </a>
                  </div>
                ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10,
                            flexWrap: 'wrap' }}>
                <select value={depot.nature} onChange={(e) => setDepot({ nature: e.target.value })}
                        style={{ padding: '7px 10px', border: '1px solid #dfe3ea', borderRadius: 6,
                                 fontSize: 12.5, color: '#263238' }}>
                  <option value="identite">Pièce d’identité</option>
                  <option value="passeport">Passeport</option>
                  <option value="justificatif_domicile">Justificatif de domicile</option>
                  <option value="releve_compte">Relevé de compte</option>
                  <option value="courrier">Courrier reçu</option>
                  <option value="contrat">Contrat</option>
                  <option value="autre">Autre</option>
                </select>
                <label style={{ padding: '7px 12px', border: '1px solid #dfe3ea', borderRadius: 6,
                                fontSize: 12.5, cursor: 'pointer', color: '#263238',
                                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Upload size={13} /> {enCours === 'depot' ? 'Dépôt…' : 'Ajouter un document'}
                  <input type="file" multiple style={{ display: 'none' }}
                         onChange={(e) => deposer(e.target.files)} />
                </label>
              </div>

              <Titre aide="Sans adresse, une demande ne peut pas partir. Vous pouvez la renseigner ici, le client peut aussi le faire depuis son espace.">
                Organismes dont l’adresse manque
                ({organismes.filter((o) => !o.email_dpo && !o.email_service_client).length})</Titre>
              <button onClick={() => setManquants(!manquantsOuverts)}
                      style={{ background: 'none', border: 'none', color: '#3949ab', fontSize: 12.5,
                               cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center',
                               gap: 5 }}>
                <ChevronDown size={14} style={{ transform: manquantsOuverts ? 'rotate(180deg)' : 'none' }} />
                {manquantsOuverts ? 'Masquer la liste' : 'Voir et compléter la liste'}
              </button>
              {manquantsOuverts && (
                <div style={{ marginTop: 10, border: '1px solid #eceff1', borderRadius: 8,
                              maxHeight: 340, overflow: 'auto' }}>
                  {organismes.filter((o) => !o.email_dpo && !o.email_service_client).map((o) => (
                    <div key={o.id} style={{ padding: '10px 12px', borderBottom: '1px solid #f4f6f8' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <b style={{ fontSize: 13 }}>{o.nom}</b>
                        <span style={{ fontSize: 11.5, color: '#90a4ae' }}>{o.categorie}</span>
                        {o.demandes_bloquees > 0 && (
                          <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 999,
                                         padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            {o.demandes_bloquees} demande{o.demandes_bloquees > 1 ? 's' : ''} bloquée
                            {o.demandes_bloquees > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {o.adresse_postale && (
                        <p style={{ margin: '2px 0 6px', fontSize: 12, color: '#78909c' }}>
                          Adresse postale connue : {o.adresse_postale}</p>
                      )}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        <input placeholder="adresse email du DPO"
                               value={(saisie[o.id] || {}).email_dpo || ''}
                               onChange={(e) => setSaisie((x) => ({ ...x,
                                 [o.id]: { ...(x[o.id] || {}), email_dpo: e.target.value } }))}
                               style={{ flex: '1 1 240px', padding: '7px 10px',
                                        border: '1px solid #dfe3ea', borderRadius: 6,
                                        fontSize: 12.5, color: '#263238' }} />
                        <button onClick={() => enregistrerContact(o.id)}
                                style={{ padding: '7px 12px', border: 'none', borderRadius: 6,
                                         background: '#000091', color: '#fff', fontSize: 12.5,
                                         fontWeight: 600, cursor: 'pointer',
                                         display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Plus size={13} /> Enregistrer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Titre aide="Chaque étape franchie depuis l’ouverture du dossier : c’est ici qu’on voit où il s’est arrêté.">
                Son parcours</Titre>
              {(!d.parcours || d.parcours.length === 0)
                ? <p style={{ color: '#90a4ae', fontSize: 13 }}>Aucune trace enregistrée.</p>
                : d.parcours.map((e) => {
                  const n = NIVEAU[e.niveau] || NIVEAU.info;
                  return (
                    <div key={e.id} style={{ borderLeft: `3px solid ${n.bord}`, paddingLeft: 11,
                                             margin: '0 0 10px' }}>
                      <b style={{ fontSize: 13, color: n.couleur }}>
                        {(d.etapes && d.etapes[e.etape]) || e.titre || e.etape}</b>
                      <p style={{ margin: '2px 0', fontSize: 12.5, color: '#546e7a' }}>{e.detail}</p>
                      <small style={{ color: '#90a4ae' }}>
                        {dateHeure(e.cree_le)}{e.reference ? ` · ${e.reference}` : ''}</small>
                    </div>
                  );
                })}

              <Titre aide="Tout ce que le client a vu s’afficher dans son espace.">Son historique</Titre>
              {d.alertes.length === 0
                ? <p style={{ color: '#90a4ae', fontSize: 13 }}>Aucun événement.</p>
                : d.alertes.map((a) => (
                  <div key={a.id} style={{ borderLeft: '3px solid #c5cae9', paddingLeft: 11,
                                           margin: '0 0 10px' }}>
                    <b style={{ fontSize: 13 }}>{a.titre}</b>
                    <p style={{ margin: '2px 0', fontSize: 12.5, color: '#546e7a' }}>{a.message}</p>
                    <small style={{ color: '#90a4ae' }}>{dateHeure(a.cree_le)}</small>
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── liste des dossiers ───────────────────────────── */
export default function EmandatClients() {
  const [clients, setClients] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(null);
  const [majLe, setMajLe] = useState(null);

  const charger = async () => {
    try {
      const r = await fetch(`${API}/emandat/clients`, { headers: jetonEntete() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setClients(j.clients || []); setErreur(null);
      setMajLe(new Date());
    } catch (e) { setErreur(e.message); setClients([]); }
    setChargement(false);
  };
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, []);
  useRafraichissementAuto(charger, { secondes: 45, actif: !ouvert });
  useBlocageDefilement(Boolean(ouvert));

  const liste = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => [c.nom, c.prenom, c.email, c.telephone]
      .some((v) => (v || '').toLowerCase().includes(q)));
  }, [clients, recherche]);

  const aFaire = clients.reduce((t, c) => t + Number(c.services_a_faire || 0), 0);

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '22px 24px 28px',
                  boxShadow: '0 4px 18px rgba(0,0,0,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: 0, fontSize: 20,
                     color: '#263238' }}>
          <Shield size={20} color="#000091" /> Dossiers E-Mandat
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                         color: '#2e7d32', background: '#f1f8f2', border: '1px solid #c8e6c9',
                         borderRadius: 999, padding: '4px 11px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2e7d32' }} />
            Mise à jour automatique{majLe ? ` · dernière à ${majLe.toLocaleTimeString('fr-FR')}` : ''}
          </span>
          <button onClick={charger}
                  style={{ padding: '9px 12px', border: '1px solid #dfe3ea', borderRadius: 6,
                           background: '#fff', fontSize: 13.5, display: 'flex', alignItems: 'center',
                           gap: 6, cursor: 'pointer' }}>
            <RefreshCw size={14} /> Actualiser maintenant
          </button>
        </div>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#90a4ae' }}>
        Le suivi des dossiers du service de vérification de contrats. Chaque fiche contient
        l’identité du client, son abonnement, son mandat, ses demandes et tout ce qu’il vous a confié.
      </p>

      {aFaire > 0 && (
        <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8,
                      padding: '11px 14px', marginBottom: 16, fontSize: 13, color: '#e65100',
                      display: 'flex', gap: 9, alignItems: 'center' }}>
          <AlertTriangle size={17} />
          <div><b>{aFaire} démarche{aFaire > 1 ? 's' : ''}</b> confiée{aFaire > 1 ? 's' : ''} par vos
            clients {aFaire > 1 ? 'attendent' : 'attend'} votre prise en charge.</div>
        </div>
      )}

      <PanneauParcours />

      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 14 }}>
        <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#b0bec5' }} />
        <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
               placeholder="Rechercher un client…"
               style={{ width: '100%', padding: '9px 12px 9px 34px', border: '1px solid #dfe3ea',
                        borderRadius: 6, fontSize: 13.5 }} />
      </div>

      {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                               borderRadius: 6, marginBottom: 14 }}>
        Impossible de joindre E-Mandat : {erreur}</div>}

      {chargement ? <p style={{ color: '#90a4ae' }}>Chargement…</p> : (
        <>
          <p style={{ color: '#78909c', fontSize: 12.5, marginBottom: 8 }}>
            {liste.length} dossier{liste.length > 1 ? 's' : ''} — cliquez pour ouvrir la fiche complète
          </p>
          <div style={{ border: '1px solid #eceff1', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead><tr>
                {['Client', 'Abonnement', 'Demandes', 'Réponses', 'Confié à traiter', 'Dernière activité'].map((t) => (
                  <th key={t} style={{ textAlign: 'left', padding: '11px 12px', background: '#f7f8fb',
                                       borderBottom: '1px solid #eceff1', fontSize: 11,
                                       textTransform: 'uppercase', letterSpacing: '.05em',
                                       color: '#78909c' }}>{t}</th>))}
              </tr></thead>
              <tbody>
                {liste.map((c, i) => (
                  <tr key={c.id} onClick={() => setOuvert(c.id)}
                      style={{ cursor: 'pointer', background: i % 2 ? '#fcfcfd' : '#fff',
                               borderBottom: '1px solid #f3f5f8' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f1fb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 ? '#fcfcfd' : '#fff'; }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: '#263238' }}>
                        {`${c.prenom || ''} ${c.nom || ''}`.trim() || '—'}</div>
                      <div style={{ color: '#90a4ae', fontSize: 11.5 }}>{c.email}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Pastille table={STATUT_CLIENT} valeur={c.statut} />
                      {c.mandat_revoque_le && <div style={{ color: '#c62828', fontSize: 11,
                        marginTop: 3 }}>mandat révoqué</div>}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#546e7a' }}>
                      {c.demandes_envoyees}/{c.demandes} envoyées</td>
                    <td style={{ padding: '10px 12px', color: '#546e7a' }}>{c.reponses}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {Number(c.services_a_faire) > 0
                        ? <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 999,
                                         padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                            {c.services_a_faire} à traiter</span>
                        : <span style={{ color: '#b0bec5' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#546e7a', whiteSpace: 'nowrap' }}>
                      {dateHeure(c.derniere_activite)}</td>
                  </tr>
                ))}
                {!liste.length && (
                  <tr><td colSpan={6} style={{ padding: 26, textAlign: 'center', color: '#90a4ae' }}>
                    Aucun dossier.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {ouvert && <Dossier clientId={ouvert} onFermer={() => setOuvert(null)} onChangement={charger} />}
    </div>
  );
}
